import { isTeamCoOwnerActor, isTeamOwnerActor } from './teamConfig';
import {
  getLifecycleModuleLabel,
  getRecordTypeLabel,
  getEndEventLabel,
  getArchiveActionLabel,
  getArchiveActionDefinition,
  getEndEventDefinition,
  getEndEventsForScope,
  getArchiveActionsForScope,
  findDuplicateLifecycleRule,
  findOverlappingLifecycleRule,
  findConflictingLifecycleRule,
  buildLifecycleScopeKey,
  normalizeLifecycleForm,
  normalizeLifecycleRuleScope,
  nextLifecycleRuleId,
  formatLifecycleConditionsDisplay,
  recordTypesForModule,
} from './lifecycleConfig';
import { emitConfigurationHistoryBatch } from './changeHistoryEmitters';

const ACTOR = {
  name: 'Olivia Brown',
  role: 'Configuration Admin',
  accountId: 'ADM-0002',
};

const TRACKED_FIELDS = [
  'ruleName',
  'module',
  'recordType',
  'endEvent',
  'archiveAction',
  'conditions',
];

export function lifecycleActor() {
  return { ...ACTOR };
}

export function canAccessLifecycleConfiguration(auth) {
  return isTeamOwnerActor(auth) || isTeamCoOwnerActor(auth);
}

export function canViewLifecycleConfiguration(auth) {
  return canAccessLifecycleConfiguration(auth);
}

export function canAddLifecycleRule(auth) {
  return canAccessLifecycleConfiguration(auth);
}

export function canEditLifecycleRule(auth) {
  return canAccessLifecycleConfiguration(auth);
}

export function canSaveLifecycleRule(auth) {
  return canAccessLifecycleConfiguration(auth);
}

function addError(result, key, message) {
  if (!result.errors[key]) result.errors[key] = message;
  if (!result.summary.includes(message)) result.summary.push(message);
}

function isBlank(value) {
  return value == null || !String(value).trim();
}

function namesMatch(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function ruleSnapshotFromForm(formData) {
  const form = normalizeLifecycleForm(formData || {});
  return {
    ruleName: form.ruleName?.trim() || '',
    module: form.module,
    recordType: form.recordType,
    endEvent: form.endEvent,
    lifecycleTrigger: form.endEvent,
    archiveAction: form.archiveAction,
    archiveRule: form.archiveAction,
    conditions: Array.isArray(form.conditions) ? form.conditions : [],
    changeReason: form.changeReason?.trim() || '',
  };
}

export function getPublishedRule(rule) {
  if (!rule) return null;
  const { pendingDraft, ...published } = rule;
  return normalizeLifecycleForm(published);
}

export function getEditRuleSource(rule) {
  if (!rule) return null;
  return normalizeLifecycleForm({ ...rule, pendingDraft: null });
}

function fieldLabel(key) {
  const labels = {
    ruleName: 'Rule Name',
    module: 'Module',
    recordType: 'Record Type',
    endEvent: 'End Event',
    archiveAction: 'Archive Action',
    conditions: 'Conditions',
    changeReason: 'Change Reason',
  };
  return labels[key] || key;
}

function displayFieldValue(key, value, context = {}) {
  if (key === 'conditions') return formatLifecycleConditionsDisplay({ conditions: value }).text;
  if (value == null || value === '') return '—';
  if (key === 'module') return getLifecycleModuleLabel(value);
  if (key === 'recordType') return getRecordTypeLabel(context.module, value);
  if (key === 'endEvent' || key === 'lifecycleTrigger') return getEndEventLabel(value);
  if (key === 'archiveAction' || key === 'archiveRule') return getArchiveActionLabel(value);
  return String(value);
}

export function diffRuleSnapshots(prev, next) {
  const changes = [];
  TRACKED_FIELDS.forEach((key) => {
    const oldDisplay = displayFieldValue(key, prev?.[key], prev || {});
    const newDisplay = displayFieldValue(key, next?.[key], next || {});
    if (oldDisplay !== newDisplay) {
      changes.push({ field: fieldLabel(key), previousValue: oldDisplay, newValue: newDisplay });
    }
  });
  return changes;
}

export function appendConfigurationHistory(rule, action, changes, status, version, changeReason = '') {
  const ts = new Date().toISOString();
  const actor = lifecycleActor();
  const base = Array.isArray(rule.configurationHistory) ? rule.configurationHistory : [];
  const changeRows = changes.length ? changes : [{ field: '—', previousValue: '—', newValue: '—' }];
  const entries = changeRows.map((c) => ({
    ruleId: rule.id,
    version,
    action,
    field: c.field,
    previousValue: c.previousValue,
    newValue: c.newValue,
    status,
    actor: actor.name,
    role: actor.role,
    accountId: actor.accountId,
    timestamp: ts,
    changeReason,
  }));
  try {
    emitConfigurationHistoryBatch({
      rows: entries,
      module: 'Configuration — Lifecycle Control',
      sourceModuleRef: 'configuration_lifecycle',
      changedItem: rule.ruleName || rule.id,
      itemId: rule.id,
      actor: { name: actor.name, role: actor.role },
      stamp: ts,
      changeType: action === 'Add' || action === 'Created' ? 'Add' : 'Edit',
      changeReason,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
  return [...base, ...entries];
}

export function appendTestHistory(rule, summary, passed) {
  const actor = lifecycleActor();
  const base = Array.isArray(rule.testHistory) ? rule.testHistory : [];
  return [
    ...base,
    {
      actor: actor.name,
      role: actor.role,
      accountId: actor.accountId,
      timestamp: new Date().toISOString(),
      result: passed ? 'passed' : 'blocked',
      summary,
    },
  ];
}

export function collectLifecycleSaveErrors(formData, allRules, excludeId, options = {}) {
  const result = { errors: {}, summary: [] };
  const form = normalizeLifecycleForm(formData || {});
  const scope = normalizeLifecycleRuleScope(form.module, form.recordType);

  if (options.canSave === false) {
    addError(result, 'permission', 'You do not have permission to save this Rule.');
    return result;
  }
  if (options.checksAvailable === false) {
    addError(result, 'checksUnavailable', 'Required checks could not be completed. Try again.');
    return result;
  }

  if (isBlank(form.ruleName)) addError(result, 'ruleName', 'Enter a unique Rule Name.');
  else {
    const clash = (allRules || []).find(
      (r) => r.id !== excludeId && namesMatch(r.ruleName, form.ruleName),
    );
    if (clash) addError(result, 'ruleName', 'Enter a unique Rule Name.');
  }

  if (!scope.module) addError(result, 'module', 'Select a Module.');
  const recordTypes = recordTypesForModule(scope.module);
  if (!scope.recordType) addError(result, 'recordType', 'Select a compatible Record Type.');
  else if (scope.module && !recordTypes.some((o) => o.value === scope.recordType)) {
    addError(result, 'recordType', 'Select a compatible Record Type.');
  }

  const endEvents = getEndEventsForScope(scope.module, scope.recordType);
  if (!form.endEvent) {
    addError(
      result,
      'endEvent',
      endEvents.length ? 'Select a registered End Event.' : 'No compatible choices available',
    );
  } else if (!getEndEventDefinition(form.endEvent)) {
    addError(result, 'endEvent', 'This Event cannot supply the required matching data.');
  } else if (endEvents.length && !endEvents.some((o) => o.value === form.endEvent)) {
    addError(result, 'endEvent', 'Select a registered End Event.');
  }

  const actions = getArchiveActionsForScope(scope.module, scope.recordType, form.endEvent);
  if (!form.archiveAction) {
    addError(
      result,
      'archiveAction',
      actions.length ? 'Select an approved Archive Action.' : 'No compatible choices available',
    );
  } else {
    const actionDef = getArchiveActionDefinition(form.archiveAction);
    if (!actionDef) {
      addError(result, 'archiveAction', 'This Archive Action or its destination is unavailable or incompatible.');
    } else if (actions.length && !actions.some((o) => o.value === form.archiveAction)) {
      addError(result, 'archiveAction', 'This Archive Action or its destination is unavailable or incompatible.');
    } else if ((actionDef.references || []).some((ref) => ref.available === false)) {
      addError(result, 'archiveAction', 'This reference is unavailable or incompatible.');
    }
  }

  if (isBlank(form.changeReason)) addError(result, 'changeReason', 'Enter a Change Reason.');

  const conditions = Array.isArray(form.conditions) ? form.conditions : [];
  conditions.forEach((condition, index) => {
    if (!condition.field || !condition.operator) {
      addError(result, `condition_${index}`, 'Complete or remove this Condition.');
    }
  });

  const candidate = { ...form, module: scope.module, recordType: scope.recordType };

  const dup = findDuplicateLifecycleRule(allRules, candidate, excludeId);
  if (dup) addError(result, 'duplicate', `This Rule duplicates ${dup.id}.`);

  const overlap = findOverlappingLifecycleRule(allRules, candidate, excludeId);
  if (overlap && (!dup || overlap.id !== dup.id)) {
    addError(result, 'overlap', `This Rule overlaps ${overlap.id}.`);
  }

  const conflict = findConflictingLifecycleRule(allRules, candidate, excludeId);
  if (conflict) addError(result, 'conflict', `This Rule conflicts with ${conflict.id}.`);

  if (excludeId && options.originalLastUpdated) {
    const current = (allRules || []).find((rule) => rule.id === excludeId);
    const currentUpdated = current?.lastUpdated || current?.modifiedOn;
    if (currentUpdated && currentUpdated !== options.originalLastUpdated) {
      addError(result, 'concurrent', 'This Rule has changed. Review the latest saved Rule.');
    }
  }

  return result;
}

/** @deprecated */
export function collectActivationErrors(formData, allRules, excludeId) {
  return collectLifecycleSaveErrors(formData, allRules, excludeId).summary;
}

export function runLifecycleRuleTest(formData, allRules) {
  const form = normalizeLifecycleForm(formData || {});
  const lines = [
    `Module: ${getLifecycleModuleLabel(form.module) || '—'}`,
    `Record Type: ${getRecordTypeLabel(form.module, form.recordType) || '—'}`,
    `End Event: ${getEndEventLabel(form.endEvent) || '—'}`,
    `Archive Action: ${getArchiveActionLabel(form.archiveAction) || '—'}`,
    `Conditions: ${formatLifecycleConditionsDisplay(form).text}`,
  ];
  const validation = collectLifecycleSaveErrors(form, allRules, form.id || undefined);
  if (validation.summary.length) {
    return {
      passed: false,
      summary: 'Rule test blocked. Configuration conflicts or missing requirements were found.',
      detail: [...lines, '', 'Issues:', ...validation.summary.map((c) => `• ${c}`)].join('\n'),
    };
  }
  return {
    passed: true,
    summary: 'Rule test passed. Configuration is valid — no Archive movement was performed.',
    detail: [...lines, '', 'No duplicate, overlapping or conflicting Rules detected.'].join('\n'),
  };
}

function snapshotsEqual(a, b) {
  return diffRuleSnapshots(a, b).length === 0;
}

export function mergeRulesAfterSave(allRules, payload, publishedBaseline) {
  const snapshot = ruleSnapshotFromForm(payload);
  const isNew = !publishedBaseline?.id;
  const actor = lifecycleActor();
  const nowIso = payload.lastUpdated || payload.modifiedOn || new Date().toISOString();

  if (!isNew) {
    const prevSnap = ruleSnapshotFromForm(publishedBaseline);
    if (snapshotsEqual(prevSnap, snapshot)) {
      return {
        rules: allRules,
        record: getPublishedRule(publishedBaseline),
        unchanged: true,
        isNew: false,
      };
    }
  }

  const id = isNew ? payload.id || nextLifecycleRuleId(allRules) : publishedBaseline.id;
  const changes = isNew
    ? diffRuleSnapshots({}, snapshot)
    : diffRuleSnapshots(ruleSnapshotFromForm(publishedBaseline), snapshot);

  const record = {
    ...(isNew ? {} : getPublishedRule(publishedBaseline)),
    ...snapshot,
    id,
    version: isNew ? 1 : publishedBaseline.version || 1,
    pendingDraft: null,
    configurationHistory: appendConfigurationHistory(
      {
        id,
        ruleName: snapshot.ruleName,
        configurationHistory: publishedBaseline?.configurationHistory || [],
      },
      isNew ? 'Add' : 'Edit',
      changes,
      'saved',
      isNew ? 1 : publishedBaseline.version || 1,
      snapshot.changeReason,
    ),
    testHistory: payload.testHistory || publishedBaseline?.testHistory || [],
    dateAdded: isNew ? nowIso : publishedBaseline.dateAdded || publishedBaseline.createdOn || nowIso,
    addedBy: isNew ? actor.name : publishedBaseline.addedBy || publishedBaseline.createdBy || actor.name,
    lastUpdated: nowIso,
    lastUpdatedBy: actor.name,
    createdOn: isNew ? nowIso : publishedBaseline.createdOn || publishedBaseline.dateAdded || nowIso,
    createdBy: isNew ? actor.name : publishedBaseline.createdBy || publishedBaseline.addedBy || actor.name,
    modifiedOn: nowIso,
    modifiedBy: actor.name,
  };

  const rules = isNew
    ? [...allRules.filter((r) => r.id !== record.id), record]
    : allRules.map((r) => (r.id === record.id ? record : r));

  return { rules, record, unchanged: false, isNew };
}

export function saveLifecycleRule(allRules, payload, publishedBaseline, options = {}) {
  const excludeId = publishedBaseline?.id || payload?.id || undefined;
  const validation = collectLifecycleSaveErrors(payload, allRules, excludeId, {
    originalLastUpdated:
      options.originalLastUpdated
      ?? publishedBaseline?.lastUpdated
      ?? publishedBaseline?.modifiedOn,
    originalScopeKey:
      options.originalScopeKey
      ?? (publishedBaseline ? buildLifecycleScopeKey(publishedBaseline) : undefined),
    canSave: options.canSave !== false,
    checksAvailable: options.checksAvailable !== false,
  });

  if (validation.summary.length) {
    return {
      success: false,
      errors: validation.errors,
      summary: validation.summary,
      rules: allRules,
      unchanged: false,
    };
  }

  try {
    const merged = mergeRulesAfterSave(allRules, payload, publishedBaseline || {});
    return {
      success: true,
      errors: {},
      summary: [],
      rules: merged.rules,
      record: merged.record,
      unchanged: Boolean(merged.unchanged),
      isNew: Boolean(merged.isNew),
    };
  } catch {
    return {
      success: false,
      errors: { form: 'The Rule could not be saved. Your changes have been kept for review.' },
      summary: ['The Rule could not be saved. Your changes have been kept for review.'],
      rules: allRules,
      unchanged: false,
    };
  }
}

export function mergeRulesAfterActivate(allRules, payload, publishedBaseline) {
  const result = saveLifecycleRule(
    allRules,
    { ...payload, changeReason: payload.changeReason || 'Save from configuration test' },
    publishedBaseline || {},
    { canSave: true, checksAvailable: true },
  );
  return result.rules;
}

export function mergeRulesAfterSaveDraft(allRules, payload, publishedBaseline) {
  return mergeRulesAfterActivate(allRules, payload, publishedBaseline);
}

export function mergeRulesAfterInactivate(allRules) {
  return allRules;
}

export function mergeRulesAfterListActivate(allRules) {
  return allRules;
}
